import React, { useState, useEffect } from "react";
import styles from "./WarningZoneForm.module.css";
import Btn from "../../Btn/Btn";
import { useWarningZoneContext } from "../../../contexts/WarningZoneContext";
import { useSidebarContext } from "../../../contexts/SidebarContext";
import { useAppContext } from "../../../contexts/AppContext";
import { useMapDisplayContext } from "../../../contexts/MapDisplayContext";

import { mapBounds } from "../../../constants/Map";

type WarningZoneFormProps = {
	type: "create" | "edit";
};

const severityOptions = [
	{ value: "normal", label: "Nhỏ (150km)", severity: 1, radius: 150000 },
	{ value: "strong", label: "Trung bình (300km)", severity: 2, radius: 300000 },
	{ value: "very_strong", label: "Lớn (450km)", severity: 3, radius: 450000 },
	{ value: "super_strong", label: "Rất lớn (600km)", severity: 4, radius: 600000 },
];

function toLocalDatetimeInput(ms: number) {
	const d = new Date(ms);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function clampLatLng(lat: number, lon: number): [number, number] {
	// Clamp latitude and longitude to mapBounds
	const bounds = mapBounds.pad(-0.01); // Slightly inside the bounds
	const south = bounds.getSouth();
	const north = bounds.getNorth();
	const west = bounds.getWest();
	const east = bounds.getEast();
	const clampedLat = Math.max(south, Math.min(lat, north));
	const clampedLon = Math.max(west, Math.min(lon, east));
	return [clampedLat, clampedLon];
}

const WarningZoneForm: React.FC<WarningZoneFormProps> = ({ type }) => {
	const { warningZones, setWarningZones } = useWarningZoneContext();
	const { draggableCirclePosition, chosenWarningZoneId: warningZoneBeingEditedId, setChosenWarningZoneId, setContentType } = useSidebarContext();
	const { showDialog } = useAppContext();

	// Nếu là edit, lấy zone hiện tại
	const zone = type == "edit" ? warningZones[warningZoneBeingEditedId] : undefined;

	// State cho form
	const [zoneName, setZoneName] = useState(zone?.name ?? "");
	const [centerLat, setCenterLat] = useState(zone?.centerLat?.toString() ?? mapBounds.getCenter().lat.toString());
	const [centerLon, setCenterLon] = useState(zone?.centerLon?.toString() ?? mapBounds.getCenter().lng.toString());
	const [severity, setSeverity] = useState(() => {
		if (!zone) return "normal";
		if (zone.severity == 2) return "strong";
		if (zone.severity == 3) return "very_strong";
		if (zone.severity == 4) return "super_strong";
		return "normal";
	});
	const [autoNotify, setAutoNotify] = useState(zone?.auto_notify || false);
	const [startTime, setStartTime] = useState(() =>
		zone ? toLocalDatetimeInput(zone.startTime) : ""
	);
	const [endTime, setEndTime] = useState(() =>
		zone ? toLocalDatetimeInput(zone.endTime) : ""
	);
	const [zoneContent, setZoneContent] = useState(zone?.content ?? "");
    // Thay đổi vòng tròn tạm trên map
    const { action } = useMapDisplayContext();
	useEffect(() => {

		action.setDraggableCircleRadius(severityOptions.find(s => s.value == severity)?.radius ?? 0);
		if(!isNaN(parseFloat(centerLat)) && !isNaN(parseFloat(centerLon)) ){
            console.log("Updating draggable circle:", parseFloat(centerLat), parseFloat(centerLon), severity);
			action.setDraggableCirclePosition(parseFloat(centerLat), parseFloat(centerLon));
		}
	}, [centerLat, centerLon, severity]);
    // Thay đổi kéo thả cho vòng tròn tạm
    const [draggable, setDraggable] = useState(false);
    useEffect(() => {
        action.setDraggableCircleDrag(draggable);
        if (draggable) {
            action.clickDraggableCircle();
        }
    }, [draggable]);
	// Thay đổi giá trị trong form khi di chuyển vòng tròn tạm
	useEffect(() => {
        console.log("Draggable circle position changed:", draggableCirclePosition.lat, draggableCirclePosition.lng);
		let lat = draggableCirclePosition.lat;
		let lon = draggableCirclePosition.lng;
		const [clampedLat, clampedLon] = clampLatLng(lat, lon);
		if (lat != clampedLat || lon != clampedLon) {
			setCenterLat(clampedLat.toString());
			setCenterLon(clampedLon.toString());
		} else {
			setCenterLat(lat.toString());
			setCenterLon(lon.toString());
		}
	},[draggableCirclePosition]);
	// Khi edit, nếu zone thay đổi thì cập nhật lại state
	useEffect(() => {
		if (type == "edit" && zone) {
			setZoneName(zone.name ?? "");
			setCenterLat(zone.centerLat?.toString() ?? "");
			setCenterLon(zone.centerLon?.toString() ?? "");
			setSeverity(
				zone.severity == 2
					? "strong"
					: zone.severity == 3
					? "very_strong"
					: zone.severity == 4
					? "super_strong"
					: "normal"
			);
			setAutoNotify(zone.auto_notify ?? false);
			setStartTime(toLocalDatetimeInput(zone.startTime));
			setEndTime(toLocalDatetimeInput(zone.endTime));
			setZoneContent(zone.content ?? "");
		}
	}, [type, zone]);

	// Xử lý submit
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const name = zoneName.trim();
		let lat = parseFloat(centerLat);
		let lon = parseFloat(centerLon);
		const [clampedLat, clampedLon] = clampLatLng(lat, lon);
		if (lat != clampedLat || lon != clampedLon) {
			showDialog("Lỗi", "Vị trí bạn nhập nằm ngoài phạm vi bản đồ.", "error");
			return;
		}
		const sevObj = severityOptions.find(s => s.value == severity) ?? severityOptions[0];
		const sev = sevObj.severity;
		const radius = sevObj.radius;
		const start = new Date(startTime);
		const end = new Date(endTime);
		if (!name || isNaN(lat) || isNaN(lon) || !startTime || !endTime) {
			showDialog("Lỗi", "Vui lòng nhập đầy đủ thông tin hợp lệ!", "error");
			return;
		}
		if (end <= start) {
			showDialog("Lỗi", "Thời gian kết thúc phải sau thời gian bắt đầu!", "error");
			return;
		}
		if (type == "create") {
			const confirm = await showDialog(
				"Xác nhận",
				`Bạn có chắc chắn muốn thêm mới vùng cảnh báo với các thông tin trên không?`,
				"confirm"
			);
			if (!confirm) return;
			const id = "zone_" + Date.now();
			setWarningZones(zs => ({
				...zs,
				[id]: {
					id,
					name,
					centerLat: lat,
					centerLon: lon,
					radius,
					severity: sev,
					auto_notify: autoNotify,
					startTime: start.getTime(),
					endTime: end.getTime(),
					content: zoneContent,
					status: 1,
				},
			}));
			setContentType("warningZoneList");
		} else if (type == "edit" && zone) {
			const confirm = await showDialog(
				"Xác nhận",
				`Bạn có chắc chắn muốn lưu các thay đổi của vùng cảnh báo ${zone.name} không?`,
				"confirm"
			);
			if (!confirm) return;
			setWarningZones(zs => ({
				...zs,
				[zone.id]: {
					...zone,
					name,
					centerLat: lat,
					centerLon: lon,
					radius,
					severity: sev,
					auto_notify: autoNotify,
					startTime: start.getTime(),
					endTime: end.getTime(),
					content: zoneContent,
				},
			}));
			setContentType("warningZoneList");
			setChosenWarningZoneId("");
		}
	};

	// Xử lý xoá
	const handleDelete = async () => {
		if (!zone) return;
		const confirm = await showDialog(
			"Xác nhận",
			`Bạn có chắc chắn muốn xoá vùng cảnh báo ${zone.name} không?`,
			"confirm"
		);
		if (!confirm) return;
		setWarningZones(zs => {
			const newZones = { ...zs };
			newZones[zone.id] = { ...newZones[zone.id], status: 3 };
			return newZones;
		});
		setContentType("warningZoneList");
        setChosenWarningZoneId("");
	};

	// Đóng form/quay lại
	const handleCancel = () => {
		setContentType("warningZoneList");
        setChosenWarningZoneId("");
	};

	// Thời gian min/max
	const nowStr = new Date().toISOString().slice(0, 16);
	const minStart = type == "create" ? nowStr : undefined;
	const minEnd = startTime ?? nowStr;
	const maxStart = endTime;

	return (
		<form className={styles.warningZoneForm + " styled-scrollbar"} style={{ maxWidth: "100%", maxHeight: "100vh" }} onSubmit={handleSubmit}>
			<h5 className="mb-3">{type === "edit" ? "Chỉnh sửa vùng cảnh báo" : "Tạo vùng cảnh báo mới"}</h5>
			<div className={styles.formScrollable + " flex-grow-1 min-vh-0 max-vh-100 styled-scrollbar"}>
				<div className={styles.mb2}>
					<label className="form-label">Tên vùng cảnh báo</label>
					<input type="text" className="form-control" required maxLength={255} placeholder="Nhập tên vùng cảnh báo" value={zoneName} onChange={e => setZoneName(e.target.value)} />
				</div>
				<div className={styles.mb2}>
					<div>
						<label className="form-label mb-0">Tâm vùng (lat, lon)</label>
                        <div className="form-check ms-auto d-flex align-items-center gap-1">
                            <input type="checkbox" className="form-check-input" id="dragToSelectPosition" onChange={e=>setDraggable(e.target.checked)}></input>
                            <label className="form-check-label" htmlFor="dragToSelectPosition">Kéo thả để chọn vị trí</label>
                        </div>
					</div>
					<div className="input-group">
											<input
												type="number"
												step="any"
												className="form-control"
												required
												maxLength={255}
												placeholder="Vĩ độ"
                                                value={centerLat}
                                                onChange={e=> setCenterLat(e.target.value)}
												onBlur={e => {
													const val = e.target.value;
													const lat = parseFloat(val);
													if (!isNaN(lat)) {
														const [clampedLat] = clampLatLng(lat, parseFloat(centerLon));
														setCenterLat(clampedLat.toString()); 
														e.target.value = clampedLat.toString();
													}
												}}
											/>
											<input
												type="number"
												step="any"
												className="form-control"
												required
												maxLength={255}
												placeholder="Kinh độ"
												value={centerLon}
												onChange={e => setCenterLon(e.target.value)}
												onBlur={e => {
													const val = e.target.value;
													const lon = parseFloat(val);
													if (!isNaN(lon)) {
														const [, clampedLon] = clampLatLng(parseFloat(centerLat), lon);
														setCenterLon(clampedLon.toString());
                                                        e.target.value = clampedLon.toString();
                                                    }
												}}
											/>
					</div>
				</div>
				<div className={styles.mb2}>
					<label className="form-label">Phạm vi</label>
					<select className="form-select" required value={severity} onChange={e => setSeverity(e.target.value)}>
						{severityOptions.map(opt => (
							<option key={opt.value} value={opt.value}>{opt.label}</option>
						))}
					</select>
				</div>
				<div className={styles.mb2 + " form-check d-flex align-items-center gap-1"}>
					<input type="checkbox" className="form-check-input" id="autoNotify" checked={autoNotify} onChange={e => setAutoNotify(e.target.checked)} />
					<label className="form-check-label" htmlFor="autoNotify">Tự động gửi cảnh báo</label>
				</div>
				<div className={styles.mb2}>
					<label className="form-label">Cảnh báo bắt đầu</label>
					<input type="datetime-local" className="form-control" required maxLength={255} value={startTime} min={minStart} max={maxStart} onChange={e => setStartTime(e.target.value)} />
				</div>
				<div className={styles.mb2}>
					<label className="form-label">Cảnh báo kết thúc</label>
					<input type="datetime-local" className="form-control" required maxLength={255} value={endTime} min={minEnd} onChange={e => setEndTime(e.target.value)} />
				</div>
				<div className={styles.mb2}>
					<label className="form-label">Nội dung cảnh báo</label>
					<textarea className={"form-control styled-scrollbar " + styles.textarea} required maxLength={255} placeholder="Nhập nội dung cảnh báo" rows={3} value={zoneContent} onChange={e => setZoneContent(e.target.value)} />
				</div>
				<div className={"d-flex justify-content-between gap-2 mt-3"}>
					<div>
						{type == "edit" && zone && zone.startTime > Date.now() && (
							<Btn type="button" className="btn text btn-delete-warning-zone" onClick={handleDelete}>Xoá</Btn>
						)}
					</div>
					<div className="d-flex gap-2">
						<Btn type="button" className="btn btn-cancel text" onClick={handleCancel}>Quay lại</Btn>
						<Btn type="submit" className="btn text">{type == "edit" ? "Lưu" : "Thêm mới"}</Btn>
					</div>
				</div>
			</div>
		</form>
	);
};

export default WarningZoneForm;
